import React from 'react';
import { View, Modal, TouchableWithoutFeedback, TouchableOpacity } from 'react-native';
import { Text } from '@shared/components/text';
import { Head } from '@shared/components/head';
import Button from '@shared/components/md-design-system/Button/Button';
import Icon from '@shared/components/md-design-system/Icon/Icon';
import { colorPalette } from '@shared/config/colorsConfig';

interface ConfirmModalProps {
  visible: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmVariant?: 'danger' | 'danger-outline' | 'primary';
  isLoading?: boolean;
  loadingLabel?: string;
  showWarningIcon?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  visible,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  confirmVariant = 'danger',
  isLoading = false,
  loadingLabel,
  showWarningIcon = false,
}) => {
  if (!visible) return null;

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onCancel}>
      <TouchableWithoutFeedback onPress={onCancel}>
        <View className="absolute inset-0 bg-black/45" />
      </TouchableWithoutFeedback>
      <View className="absolute inset-0 justify-center items-center px-48">
        <View className="bg-white rounded-2xl p-6 w-full">
          <TouchableOpacity
            onPress={onCancel}
            className="absolute top-4 right-4 p-1"
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Icon name="close" size={18} color={colorPalette.gray[500]} />
          </TouchableOpacity>

          {showWarningIcon && (
            <View className="items-center mb-6">
              <View className="w-24 h-24 rounded-full bg-warning-500 justify-center items-center">
                <Icon name="exclamation" size={52} color={colorPalette.white} />
              </View>
            </View>
          )}

          <Head type="h3" className="text-gray-900 mb-2 text-center">{title}</Head>
          <Text className="text-sm text-gray-600 mb-6 text-center">{message}</Text>
          <View className="flex-row gap-3">
            <View className="flex-1">
              <Button variant="outline" onPress={onCancel} disabled={isLoading}>
                {cancelLabel}
              </Button>
            </View>
            <View className="flex-1">
              <Button variant={confirmVariant} onPress={onConfirm} disabled={isLoading}>
                {isLoading && loadingLabel ? loadingLabel : confirmLabel}
              </Button>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default ConfirmModal;
